
import Vue from 'vue';
import isMobileView from '@root/common/utils/isMobileView';
import { Podcast, PodcastCategory } from '@root/modules/podcast/types/podcast';
import ContentImage from '@core/components/UI/ContentImage.vue';
import LinkHandler from '@core/components/helpers/LinkHandler.vue';
import categoryUrl from '@root/modules/category/utils/buildCategoryUrl';
import { CustomCrop } from '@root/common/types/fragment';
import getItemFromObject from 'lodash/get';
import { stripTags } from '@root/common/filters/htmlFormater';
import getDurationString from '@root/modules/podcast/utils/getDurationString';

interface Srcset {
  w?: number;
  h?: number;
  r?: string;
}

interface PictureSrcset {
  xs: Srcset;
  sm: Srcset;
  lg: Srcset;
}

interface Props {
  podcast: Podcast;
  isBook: boolean;
  isSearchResult: boolean;
}

interface Data {
  isMobileView: boolean;
}

interface Methods {
  getCategoryHref: (category: PodcastCategory) => string;
  getCropperData(): Record<string, unknown> | null;
  getCustomCrop(ratio: string): CustomCrop | undefined;
  getDurationString: typeof getDurationString;
  getFocuspoint(): Record<string, unknown>;
  getItemFromObject: typeof getItemFromObject;
  getTitle: (podcast: Podcast, strip: boolean) => string;
  handleClick: () => void;
}

const pictureSrcset: PictureSrcset = { lg: { w: 450, h: 450, r: '1:1' }, sm: { w: 282, h: 282, r: '1:1' }, xs: { w: 120, h: 120, r: '1:1' } };
const bookPictureSrcset: PictureSrcset = { lg: { w: 450, h: 674, r: '2:3' }, sm: { w: 282, h: 423, r: '2:3' }, xs: { w: 120, h: 180, r: '2:3' } };
const sizeList: ['lg', 'sm', 'xs'] = ['lg', 'sm', 'xs'];

export default Vue.extend<Data, Methods, unknown, Props>({
  components: {
    ContentImage,
    LinkHandler,
  },
  props: {
    podcast: {
      type: Object,
      required: true,
    },
    isBook: {
      type: Boolean,
      required: false,
      default: false,
    },
    isSearchResult: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      isMobileView: false,
    };
  },
  methods: {
    getCategoryHref(category) {
      return categoryUrl({ category, domain: 'www.delfi.lt/fone', fakeDomainEnabled: this.$config.FAKE_DOMAIN_ENABLED });
    },
    getCustomCrop(ratio) {
      const image = this.podcast.metaImage;
      if (!image) {
        return undefined;
      }

      const customCrops = image!.attributes?.cropperData?.customCrops || [];

      return customCrops.find((crop: { ratio: string }) => crop.ratio === ratio);
    },
    getDurationString,
    getFocuspoint() {
      const cropperProps: Record<string, unknown> = {};

      const image = this.podcast.metaImage;

      const focuspoint = image?.attributes?.cropperData?.focuspoint;
      if (focuspoint?.x !== undefined && focuspoint?.x !== null) {
        cropperProps.fx = focuspoint.x;
      } else {
        cropperProps.fx = '0.5';
      }

      if (focuspoint?.y !== undefined && focuspoint?.y !== null) {
        cropperProps.fy = focuspoint.y;
      } else {
        cropperProps.fy = '0.5';
      }

      return cropperProps;
    },
    getCropperData() {
      if (!this.podcast) {
        return null;
      }

      const cropperData: Record<string, unknown> = {};

      for (const size of sizeList) {
        const _pictureSrcset = this.isBook ? bookPictureSrcset : pictureSrcset;
        if (_pictureSrcset?.[size]) {
          const sizeSrcset = _pictureSrcset[size];

          const customCrop = this.getCustomCrop(sizeSrcset?.r || '1:1');

          if (customCrop) {
            cropperData[size] = { ...sizeSrcset, customCrop };
          } else {
            cropperData[size] = sizeSrcset;
          }
        }
      }

      const image = this.podcast.metaImage;
      if (!image) {
        return null;
      }

      return {
        ...cropperData,
        ...(image!.attributes?.cropperData || {}),
        base: this.getFocuspoint(),
      };
    },
    getItemFromObject,
    getTitle(podcast, strip = false) {
      const title = podcast.content.title?.html
        ? podcast.content.title?.html.replace(/(<\/?h1>)/gi, '')
        : podcast.content.title?.text
          ? podcast.content.title?.text
          : '';

      return strip && typeof stripTags === 'function' ? stripTags(title) : title;
    },
    handleClick() {
      if (this.isSearchResult) {
        this.$emit('close');
      }
    },
  },
  mounted() {
    this.isMobileView = isMobileView();
  },
});
